import { useState, useEffect } from "react";
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
} from "@ionic/react";
import { Plus } from "lucide-react";
import { useStore } from "../store/useStore";
import RecurringTaskForm from "../components/recurring/RecurringTaskForm";
import RecurringTaskItem from "../components/recurring/RecurringTaskItem";
import type {
  RecurrenceRule,
  TaskUrgency,
  TaskImportance,
} from "../types";

export default function RecurringPage() {
  const [showForm, setShowForm] = useState(false);

  const recurringDefinitions = useStore((s) => s.recurringDefinitions);
  const fetchRecurringDefinitions = useStore((s) => s.fetchRecurringDefinitions);
  const createRecurringDefinition = useStore((s) => s.createRecurringDefinition);
  const deleteRecurringDefinition = useStore((s) => s.deleteRecurringDefinition);
  const pauseRecurringDefinition = useStore((s) => s.pauseRecurringDefinition);
  const resumeRecurringDefinition = useStore((s) => s.resumeRecurringDefinition);
  const loading = useStore((s) => s.loading.recurring);
  const tags = useStore((s) => s.tags);

  useEffect(() => {
    fetchRecurringDefinitions();
  }, [fetchRecurringDefinitions]);

  const handleSubmit = async (data: {
    title: string;
    description?: string;
    urgency: TaskUrgency;
    importance: TaskImportance;
    recurrence_rule: RecurrenceRule;
    tag_id?: string;
  }): Promise<boolean> => {
    const result = await createRecurringDefinition(data);
    if (result) {
      setShowForm(false);
      return true;
    }
    return false;
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/settings" />
          </IonButtons>
          <IonTitle>Recurring Tasks</IonTitle>
          <IonButtons slot="end">
            <button
              onClick={() => setShowForm(!showForm)}
              className="p-2 text-primary"
            >
              <Plus className="w-5 h-5" />
            </button>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="p-4 space-y-4">
          {/* Create form */}
          {showForm && (
            <RecurringTaskForm
              tags={tags}
              onSubmit={handleSubmit}
              onCancel={() => setShowForm(false)}
            />
          )}

          {/* Definitions list */}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 shimmer rounded-lg" />
              ))}
            </div>
          ) : recurringDefinitions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">
                No recurring tasks yet
              </p>
              <p className="text-muted-foreground text-sm mt-1">
                Tap + to create one
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {recurringDefinitions.map((def) => (
                <RecurringTaskItem
                  key={def.id}
                  definition={def}
                  onPause={pauseRecurringDefinition}
                  onResume={resumeRecurringDefinition}
                  onDelete={deleteRecurringDefinition}
                />
              ))}
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
